import { Injectable } from '@angular/core';
import { WindowRefService } from '../../core/services/windowRef.service';

interface AuiSelect2Props {
  elemSelector: string;
  options?: any;
}

export interface SelectItem {
  text: string;
  id: string | number;
  selected?: boolean;
  locked?: boolean;
  disabled?: boolean;
}

export interface SelectChangeEvent {
  val: unknown | unknown[];
}

export interface SelectSelectingEvent extends Event {
  object?: SelectItem;
}

export interface SelectChangeCallback {
  (event: SelectChangeEvent): void;
}

export interface SelectSelectingCallback {
  (event: SelectSelectingEvent): void;
}

export interface SelectFormatCallback {
  (state: SelectItem): string;
}

interface SelectProps {
  elemSelector: string;
  items: any[];
  selectedItems?: any[];
  placeholder?: string;
  changeCallback?: SelectChangeCallback;
  initSelection?(element: unknown, callback: unknown): void;
  selectingCallback?: SelectSelectingCallback;
  formatCallback?: SelectFormatCallback;
}

interface AutoOpenOnce {
  [key: string]: string;
}

@Injectable()
export class SelectService {
  autoOpenOnce: AutoOpenOnce = {};

  constructor(private windowRef: WindowRefService) {}

  // escapeMarkup: (m: unknown) => m,
  initSelect({
    elemSelector,
    placeholder,
    items,
    changeCallback,
    initSelection,
    selectingCallback,
    formatCallback,
  }: SelectProps): void {
    const options: any = {
      multiple: true,
      placeholder,
      allowClear: true,
      data: items,
      initSelection,
    };

    if (!options.initSelection) {
      // We should have it for a proper select2 initialisation
      options.initSelection = () => {};
    }
    if (typeof formatCallback === 'function') {
      options.formatResult = formatCallback;
      options.formatSelection = formatCallback;
      options.escapeMarkup = (m: unknown) => m;
    }

    const singleSelect = this.createAuiSelect2({ elemSelector, options });

    singleSelect.on('select2-close', () => {
      if (this.autoOpenOnce[elemSelector] === undefined) {
        this.windowRef.AJS.$(elemSelector).select2('open');
        this.autoOpenOnce[elemSelector] = 'on';
      }
    });

    if (typeof selectingCallback === 'function') {
      singleSelect.on('select2-selecting', selectingCallback);
    }
    if (typeof changeCallback === 'function') {
      singleSelect.on('change', changeCallback);
    }

    singleSelect.select2('val', []);
  }

  setValuesAndClose(elemSelector: string, values: unknown[]): void {
    this.autoOpenOnce[elemSelector] = 'on';
    this.windowRef.AJS.$(elemSelector).select2('val', values).trigger('change').auiSelect2('close');
  }

  setEnableDisable(elemSelector: string, flag = false): void {
    this.windowRef.AJS.$(elemSelector).select2('enable', flag);
  }

  clearAll(elemSelector: string): void {
    this.autoOpenOnce[elemSelector] = 'on';
    this.windowRef.AJS.$(elemSelector).select2('val', []).trigger('change').auiSelect2('close');
  }

  updatePlaceholder(elemSelector: string, placeholder: string): void {
    this.windowRef.AJS.$(elemSelector).auiSelect2({ placeholder });
  }

  private createAuiSelect2({ elemSelector, options }: AuiSelect2Props): any {
    return this.windowRef.AJS.$(elemSelector)
      .show()
      .val('')
      .auiSelect2('destroy')
      .auiSelect2(options || {})
      .off('change')
      .off('select2-selecting');
  }
}
